"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { navItems, filterNavItems } from "@/lib/navigation";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useTranslations } from "@/hooks/useTranslations";

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  organizationName?: string;
  organizationLogo?: string | null;
  userRole?: string;
  primaryColor?: string | null;
}

export function MobileNav({
  isOpen,
  onClose,
  organizationName = "Your Business",
  organizationLogo,
  primaryColor,
}: MobileNavProps) {
  const pathname = usePathname();
  const { features, staffPermissions } = useOrganization();
  const { isOwner, isStaff } = useAuth();
  const t = useTranslations('navigation');
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  // Translation map for navigation items
  const getTranslatedName = (name: string): string => {
    const itemMap: Record<string, string> = {
      'AI Agent': t('items.aiAgent'),
      'Dashboard': t('dashboard'),
      'Getting Started': t('items.gettingStarted'),
      'Staff Tour': t('items.staffTour'),
      'Jobs': t('jobs'),
      'Schedule': t('schedule'),
      'Routes': t('items.routes'),
      'Time Tracking': t('items.timeTracking'),
      'Dispatch': t('items.dispatch'),
      'Clients': t('clients'),
      'Quotes': t('quotes'),
      'Invoices': t('invoices'),
      'Messages': t('items.messages'),
      'Help': t('items.help'),
    };
    return itemMap[name] || name;
  };

  // Fetch pending approvals count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await fetch("/api/bookings/pending-count");
        if (response.ok) {
          const data = await response.json();
          setPendingApprovalsCount(data.count || 0);
        }
      } catch (error) {
        console.error("Failed to fetch pending approvals count:", error);
      }
    };

    fetchPendingCount();

    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter navigation based on enabled features, staff permissions, and user role
  const filteredNavItems = filterNavItems(navItems, features, staffPermissions, isOwner, isStaff);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Mobile sidebar */}
      <nav
        className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl dark:bg-gray-950 lg:hidden"
        role="navigation"
        aria-label={t('mobileNavigation')}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4 dark:border-gray-800">
            <Link href="/dashboard" className="flex items-center gap-2 min-w-0 flex-1 mr-2" onClick={onClose}>
              {organizationLogo ? (
                <img
                  src={organizationLogo}
                  alt={organizationName}
                  className="h-8 w-8 rounded flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div
                  className="flex h-8 w-8 items-center justify-center rounded font-bold text-white flex-shrink-0"
                  style={{
                    backgroundColor: primaryColor || "var(--color-primary, #3b82f6)",
                  }}
                >
                  {organizationName.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-base font-semibold truncate min-w-0">{organizationName}</span>
            </Link>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('closeNavigationMenu')} className="min-h-[44px] min-w-[44px] flex-shrink-0">
              <X className="h-6 w-6" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <div className="space-y-1">
              {filteredNavItems.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
                const Icon = item.icon;
                const isBookings = item.href === "/bookings";
                const showBadge = isBookings && pendingApprovalsCount > 0;

                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors min-h-[44px]",
                      isActive
                        ? "bg-[var(--color-primary,#3b82f6)] text-white"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    )}
                    style={
                      isActive && primaryColor
                        ? { backgroundColor: primaryColor }
                        : undefined
                    }
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="flex-1">{getTranslatedName(item.name)}</span>
                    {showBadge && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">
                        {pendingApprovalsCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer hint */}
          <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('mobileNavHint')}
            </p>
          </div>
        </div>
      </nav>
    </>
  );
}
